import { useAuth } from "@assetwatch/auth";
import { useQuery } from "@tanstack/react-query";
import { useFeatureFlags } from "../../components/LaunchDarkly/useFeatureFlags";
import { fetchMpEvents, fetchMpEventTypes } from "./api";

/**
 * react-query hooks for the MP Events page's ML API endpoint.
 *
 * Both take the same two things from context — the signed-in user and the
 * feature flags — and hand them to the fetcher, which is where the access check
 * and the request live.
 */

/** The vocabulary reads no data and changes only when the backend ships a new
 * source, so navigation shouldn't re-request it; a page reload picks up
 * additions. */
const CATALOG_STALE_TIME = 30 * 60_000;
const DATA_STALE_TIME = 60_000;

/** The event-type vocabulary behind the MP Events filter dropdown. */
export function useMpEventTypes() {
  const { user } = useAuth();
  const flags = useFeatureFlags();

  return useQuery({
    queryKey: ["mpEventTypes"],
    queryFn: () => fetchMpEventTypes({ user, flags }),
    staleTime: CATALOG_STALE_TIME,
    meta: { errorMessage: "Could not load the event type list." },
  });
}

export type MpEventsFilters = {
  daysBack: number;
  eventType?: string;
  monitoringPointId?: string;
  search?: string;
};

/**
 * Events for a set of applied filters. Every call is a real Athena scan, so the
 * query is disabled until the page has filters to apply — nothing fetches on
 * mount.
 */
export function useMpEvents(filters: MpEventsFilters | undefined) {
  const { user } = useAuth();
  const flags = useFeatureFlags();

  return useQuery({
    queryKey: [
      "mpEvents",
      filters?.daysBack ?? null,
      filters?.eventType ?? null,
      filters?.monitoringPointId ?? null,
      filters?.search ?? null,
    ],
    queryFn: () =>
      fetchMpEvents({
        user,
        flags,
        daysBack: filters?.daysBack ?? 0,
        eventType: filters?.eventType,
        monitoringPointId: filters?.monitoringPointId,
        search: filters?.search,
      }),
    enabled: Boolean(filters),
    // No retries: every attempt is a paid Athena scan, and the two things that
    // actually fail here — a rejected parameter and a response we can't read —
    // fail identically on the second and third try. "View Events" is the retry.
    retry: false,
    staleTime: DATA_STALE_TIME,
    meta: { errorMessage: "Could not load monitoring point events." },
  });
}
